import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ─── Dashboard Metrics ───────────────────────────────────────
export const fetchDashboardMetrics = createAsyncThunk(
  'adAnalytics/fetchDashboardMetrics',
  async ({ startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get('/marketing/unified/dashboard', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch dashboard metrics');
    }
  }
);

// ─── Best Platform ───────────────────────────────────────
export const fetchBestPlatform = createAsyncThunk(
  'adAnalytics/fetchBestPlatform',
  async ({ startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get('/marketing/unified/best-platform', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch best platform');
    }
  }
);

// ─── Best Times to Run ───────────────────────────────────────
export const fetchBestTimes = createAsyncThunk(
  'adAnalytics/fetchBestTimes',
  async ({ days = 30 } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/unified/best-times', {
        params: { days }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch best times');
    }
  }
);

// ─── Budget Allocation ───────────────────────────────────────
export const fetchBudgetAllocation = createAsyncThunk(
  'adAnalytics/fetchBudgetAllocation',
  async ({ totalBudget = 10000, startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      const params = { totalBudget };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get('/marketing/unified/budget-allocation', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch budget allocation');
    }
  }
);

// ─── A/B Test Results ───────────────────────────────────────
export const fetchABTestResults = createAsyncThunk(
  'adAnalytics/fetchABTestResults',
  async ({ campaignIds }, { rejectWithValue }) => {
    try {
      if (!campaignIds || campaignIds.length < 2) {
        return rejectWithValue('Need at least 2 campaigns to compare');
      }

      const response = await api.get('/marketing/unified/ab-test', {
        params: { campaignIds: campaignIds.join(',') }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch A/B test results');
    }
  }
);

// ─── Performance Trends ───────────────────────────────────────
export const fetchPerformanceTrends = createAsyncThunk(
  'adAnalytics/fetchPerformanceTrends',
  async ({ days = 30 } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/unified/performance-trends', {
        params: { days }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch performance trends');
    }
  }
);

// ─── Snapshots ───────────────────────────────────────────────
export const fetchSnapshots = createAsyncThunk(
  'adAnalytics/fetchSnapshots',
  async ({ days = 30, limit = 30, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/unified/snapshots', {
        params: { days, limit, offset }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch snapshots');
    }
  }
);

const initialState = {
  dashboardMetrics: null,
  bestPlatform: null,
  bestTimes: null,
  budgetAllocation: null,
  abTestResults: null,
  performanceTrends: null,
  snapshots: [],

  loading: false,
  error: null,
};

const adAnalyticsSlice = createSlice({
  name: 'adAnalytics',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Dashboard Metrics
    builder
      .addCase(fetchDashboardMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardMetrics = action.payload;
      })
      .addCase(fetchDashboardMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Best Platform
    builder
      .addCase(fetchBestPlatform.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBestPlatform.fulfilled, (state, action) => {
        state.loading = false;
        state.bestPlatform = action.payload;
      })
      .addCase(fetchBestPlatform.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Best Times
    builder
      .addCase(fetchBestTimes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBestTimes.fulfilled, (state, action) => {
        state.loading = false;
        state.bestTimes = action.payload;
      })
      .addCase(fetchBestTimes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Budget Allocation
    builder
      .addCase(fetchBudgetAllocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBudgetAllocation.fulfilled, (state, action) => {
        state.loading = false;
        state.budgetAllocation = action.payload;
      })
      .addCase(fetchBudgetAllocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // A/B Test Results
    builder
      .addCase(fetchABTestResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchABTestResults.fulfilled, (state, action) => {
        state.loading = false;
        state.abTestResults = action.payload;
      })
      .addCase(fetchABTestResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Performance Trends
    builder
      .addCase(fetchPerformanceTrends.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPerformanceTrends.fulfilled, (state, action) => {
        state.loading = false;
        state.performanceTrends = action.payload;
      })
      .addCase(fetchPerformanceTrends.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Snapshots
    builder
      .addCase(fetchSnapshots.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSnapshots.fulfilled, (state, action) => {
        state.loading = false;
        state.snapshots = action.payload.snapshots || [];
      })
      .addCase(fetchSnapshots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = adAnalyticsSlice.actions;
export default adAnalyticsSlice.reducer;
